import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  RefreshCw,
  Loader2,
  User,
  Phone,
  Mail,
  Copy,
  ExternalLink,
  ChevronDown,
  Check,
  History,
  Wallet,
  Bitcoin,
  Home,
  Download
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATES = [
  { id: 'personal', name: 'Персональные данные', icon: User, description: 'Профиль пользователя' },
  { id: 'payments', name: 'История платежей', icon: History, description: 'Транзакции и операции' },
  { id: 'main', name: 'Главная страница', icon: Home, description: 'Дашборд кошелька' }
];

export default function CashinoutTemplate() {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('personal');
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [combinedPages, setCombinedPages] = useState(false);
  const [showBrowser, setShowBrowser] = useState(true);
  const [browserTheme, setBrowserTheme] = useState('dark'); // 'dark' or 'light'
  const [addRandomOperations, setAddRandomOperations] = useState(false);
  const [isDownloadingArchive, setIsDownloadingArchive] = useState(false);
  const previewRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ozon/profiles`, {
        headers: getAuthHeaders(),
      });
      // Ensure response.data is an array
      const data = Array.isArray(response.data) ? response.data : [];
      // Prefer persistent cashinout_id from DB; fall back to legacy state
      // then to a random ID (kept only in memory for that session).
      const profilesWithIds = data.map(profile => ({
        ...profile,
        cashinoutId:
          profile.cashinout_id ||
          profile.cashinoutId ||
          Math.floor(100000 + Math.random() * 900000)
      }));
      setProfiles(profilesWithIds);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      toast.error("Ошибка загрузки профилей");
      setProfiles([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const formatPhone = (phone) => {
    if (!phone) return "+7 (999) 123-45-67";
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11) {
      return `+${digits[0]} (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7,9)}-${digits.slice(9,11)}`;
    }
    return phone;
  };

  const handleGenerate = () => {
    if (!selectedProfile) {
      toast.error("Выберите профиль OZON");
      return;
    }
    
    setIsGenerating(true);
    
    const firstName = selectedProfile.first_name || selectedProfile.name?.split(' ')[0] || "Имя";
    const lastName = selectedProfile.last_name || selectedProfile.name?.split(' ')[1] || "Фамилия";
    const phone = selectedProfile.phone || "+7 (999) 123-45-67";
    const email = selectedProfile.email !== "не установлен" ? selectedProfile.email : `${firstName.toLowerCase()}.${lastName.toLowerCase()}@mail.ru`;
    
    // Sort transactions by date (newest first)
    let sortedTransactions = [...(selectedProfile.transactions || [])].sort((a, b) => {
      const parseDate = (dateStr, timeStr) => {
        if (!dateStr) return 0;
        const parts = dateStr.split('.');
        if (parts.length === 3) {
          const [day, month, year] = parts;
          const time = timeStr || '00:00:00';
          return new Date(`${year}-${month}-${day}T${time}`).getTime();
        }
        return 0;
      };
      return parseDate(b.date, b.time) - parseDate(a.date, a.time);
    });

    // Invert transaction types for crypto template
    // Mark OZON transactions
    let transactions = sortedTransactions.map(tx => ({
      ...tx,
      type: tx.type === 'deposit' ? 'withdrawal' : 'deposit',
      crypto: getCryptoType(tx.amount),
      isOzonTx: true
    }));
    
    // Add random operations if enabled (5-6 random transactions from 1000 to 5000 RUB)
    if (addRandomOperations && transactions.length > 0) {
      // Helper to parse date
      const parseDateOnly = (dateStr) => {
        if (!dateStr) return null;
        const parts = dateStr.split('.');
        if (parts.length === 3) {
          return new Date(parts[2], parts[1] - 1, parts[0]);
        }
        return null;
      };
      
      const parseDateTime = (dateStr, timeStr) => {
        if (!dateStr) return 0;
        const parts = dateStr.split('.');
        if (parts.length === 3) {
          const [day, month, year] = parts;
          const time = timeStr || '00:00';
          return new Date(`${year}-${month}-${day}T${time}`).getTime();
        }
        return 0;
      };
      
      // Find the oldest positive OZON transaction (this will be second-to-last)
      const ozonDeposits = transactions.filter(t => t.type === 'deposit' && t.isOzonTx);
      let oldestOzonDeposit = null;
      let oldestOzonDepositTime = Infinity;
      
      for (const tx of ozonDeposits) {
        const txTime = parseDateTime(tx.date, tx.time);
        if (txTime < oldestOzonDepositTime) {
          oldestOzonDepositTime = txTime;
          oldestOzonDeposit = tx;
        }
      }
      
      // Get date range from OZON transactions
      const dates = transactions.map(t => parseDateOnly(t.date)).filter(d => d);
      let minDate = new Date();
      let maxDate = new Date();
      
      if (dates.length > 0) {
        minDate = new Date(Math.min(...dates.map(d => d.getTime())));
        maxDate = new Date(Math.max(...dates.map(d => d.getTime())));
      }
      
      // Generate 5-6 random operations (within OZON date range, but NOT older than oldest OZON deposit)
      const randomCount = Math.floor(Math.random() * 2) + 5; // 5 or 6
      const randomOperations = [];
      
      // Random operations should be between oldest OZON deposit and newest OZON transaction
      const randomMinDate = oldestOzonDeposit ? new Date(parseDateTime(oldestOzonDeposit.date, oldestOzonDeposit.time) + 24*60*60*1000) : minDate;
      
      for (let i = 0; i < randomCount; i++) {
        // Random date within range (after oldest OZON deposit)
        const randomTime = randomMinDate.getTime() + Math.random() * (maxDate.getTime() - randomMinDate.getTime());
        const randomDate = new Date(randomTime);
        
        const day = String(randomDate.getDate()).padStart(2, '0');
        const month = String(randomDate.getMonth() + 1).padStart(2, '0');
        const year = randomDate.getFullYear();
        const hour = String(Math.floor(Math.random() * 14) + 8).padStart(2, '0');
        const min = String(Math.floor(Math.random() * 60)).padStart(2, '0');
        
        // Random amount 1000-5000
        const amount = Math.floor(Math.random() * 4001) + 1000;
        
        // Random type (positive or negative)
        const isDeposit = Math.random() > 0.5;
        
        randomOperations.push({
          id: Date.now() + i,
          date: `${day}.${month}.${year}`,
          time: `${hour}:${min}`,
          type: isDeposit ? 'deposit' : 'withdrawal',
          amount: amount,
          category: isDeposit ? 'Пополнения' : 'Переводы',
          method: 'Криптовалюта',
          crypto: getCryptoType(amount),
          isOzonTx: false,
          isRandomOp: true
        });
      }
      
      // Add random operations to transactions
      transactions = [...transactions, ...randomOperations];
      
      // Create first deposit СБП (2000-10000₽) with date BEFORE the oldest OZON deposit
      const oldestOzonDate = oldestOzonDeposit ? parseDateOnly(oldestOzonDeposit.date) : minDate;
      const firstDepositDate = new Date(oldestOzonDate);
      firstDepositDate.setDate(firstDepositDate.getDate() - Math.floor(Math.random() * 5) - 3); // 3-7 days before
      
      const fdDay = String(firstDepositDate.getDate()).padStart(2, '0');
      const fdMonth = String(firstDepositDate.getMonth() + 1).padStart(2, '0');
      const fdYear = firstDepositDate.getFullYear();
      const fdHour = String(Math.floor(Math.random() * 12) + 8).padStart(2, '0');
      const fdMin = String(Math.floor(Math.random() * 60)).padStart(2, '0');
      const fdAmount = Math.floor(Math.random() * 8001) + 2000;
      
      const firstDeposit = {
        id: Date.now() + 999,
        date: `${fdDay}.${fdMonth}.${fdYear}`,
        time: `${fdHour}:${fdMin}`,
        type: 'deposit',
        amount: fdAmount,
        category: 'Пополнения',
        method: 'СБП',
        crypto: getCryptoType(fdAmount),
        isOzonTx: false,
        isFirstDeposit: true
      };
      
      transactions.push(firstDeposit);
      
      // Sort ALL transactions by date and time (newest first)
      transactions.sort((a, b) => {
        return parseDateTime(b.date, b.time) - parseDateTime(a.date, a.time);
      });
      
      // The order should now be correct:
      // - Newest transactions at top
      // - Oldest OZON deposit (second-to-last)
      // - First deposit СБП (last, oldest)
    }
    
    const data = {
      id: selectedProfile.id,
      cashinoutId: selectedProfile.cashinoutId,
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`,
      phone: formatPhone(phone),
      email,
      balance: selectedProfile.template_balance || selectedProfile.balance || 0,
      transactions,
      showBrowser,
      browserTheme,
      addRandomOperations
    };
    
    setGeneratedData(data);
    setIsGenerating(false);
    toast.success("Данные сгенерированы!");
  };

  const downloadArchive = async () => {
    if (!generatedData) return;
    
    setIsDownloadingArchive(true);
    toast.info("Создание архива скриншотов...");
    
    try {
      const response = await axios.post(
        `${API}/templates/cashinout/archive`,
        { 
          data: generatedData,
          pages: ['personal', 'payments', 'main']
        },
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Cashinout_${generatedData.cashinoutId}_${Date.now()}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Архив скриншотов скачан!");
    } catch (error) {
      console.error("Archive error:", error);
      toast.error("Ошибка создания архива");
    } finally {
      setIsDownloadingArchive(false);
    }
  };

  const getCryptoType = (amount) => {
    const types = ['USDT', 'BTC', 'ETH', 'USDC'];
    return types[Math.abs(amount) % 4];
  };

  const getPreviewUrl = () => {
    if (!generatedData) return '';
    const dataStr = encodeURIComponent(JSON.stringify(generatedData));
    
    // If combined pages mode is enabled, use combined page
    if (combinedPages) {
      return `${window.location.origin}/templates/cashinout/combined?data=${dataStr}`;
    }
    
    if (selectedTemplate === 'personal') {
      return `${window.location.origin}/templates/cashinout/preview?data=${dataStr}`;
    }
    if (selectedTemplate === 'main') {
      return `${window.location.origin}/templates/cashinout/main?data=${dataStr}`;
    }
    return `${window.location.origin}/templates/cashinout/payments?data=${dataStr}`;
  };

  const openPreview = () => {
    const url = getPreviewUrl();
    if (url) {
      window.open(url, '_blank');
    }
  };

  const copyLink = () => {
    const url = getPreviewUrl();
    if (url) {
      navigator.clipboard.writeText(url);
      toast.success("Ссылка скопирована!");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="cashinout-template-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
              <Bitcoin className="w-5 h-5 text-white" />
            </div>
            Крипто Cashinout.io
          </h1>
          <p className="text-slate-500 mt-1">
            Генератор персональной страницы из профиля OZON
          </p>
        </div>
        <Button 
          onClick={() => fetchProfiles()}
          variant="outline"
          data-testid="refresh-profiles-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Обновить профили
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Settings */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Настройки генерации</h2>
            
            {/* Template Selection */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Выберите шаблон</Label>
              <div className="grid grid-cols-3 gap-3">
                {TEMPLATES.map((template) => {
                  const Icon = template.icon;
                  return (
                    <button
                      key={template.id}
                      onClick={() => {
                        setSelectedTemplate(template.id);
                        setGeneratedData(null);
                      }}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedTemplate === template.id
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`w-6 h-6 mb-2 ${
                        selectedTemplate === template.id ? 'text-emerald-500' : 'text-slate-400'
                      }`} />
                      <div className={`font-medium ${
                        selectedTemplate === template.id ? 'text-emerald-700' : 'text-slate-700'
                      }`}>
                        {template.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{template.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Combined Pages Option */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-emerald-300 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={combinedPages}
                  onChange={(e) => {
                    setCombinedPages(e.target.checked);
                    setGeneratedData(null);
                  }}
                  className="w-5 h-5 rounded border-slate-300 text-emerald-500 focus:ring-emerald-500"
                />
                <div>
                  <div className="font-medium text-slate-700">Объединить все страницы</div>
                  <div className="text-xs text-slate-500">
                    Главная, Настройки и История в одной ссылке с навигацией через меню
                  </div>
                </div>
              </label>
            </div>

            {/* Show Browser Option */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-emerald-300 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={showBrowser}
                  onChange={(e) => {
                    setShowBrowser(e.target.checked);
                    setGeneratedData(null);
                  }}
                  className="w-5 h-5 rounded border-slate-300 text-emerald-500 focus:ring-emerald-500"
                />
                <div>
                  <div className="font-medium text-slate-700">Показывать рамку браузера</div>
                  <div className="text-xs text-slate-500">
                    Отображать Chrome-рамку с вкладками и адресной строкой
                  </div>
                </div>
              </label>
            </div>

            {/* Browser Theme Selection */}
            <div className="mb-6 p-3 bg-slate-50 rounded-lg">
              <div className="text-sm font-medium text-slate-700 mb-2">Фон браузера</div>
              <div className="flex gap-3">
                <button
                  onClick={() => { setBrowserTheme('light'); setGeneratedData(null); }}
                  className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    browserTheme === 'light'
                      ? 'border-emerald-500 bg-white text-slate-800'
                      : 'border-slate-200 bg-white text-slate-500 hover:border-emerald-300'
                  }`}
                >
                  Светлый
                </button>
                <button
                  onClick={() => { setBrowserTheme('dark'); setGeneratedData(null); }}
                  className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    browserTheme === 'dark'
                      ? 'border-emerald-500 bg-slate-800 text-white'
                      : 'border-slate-200 bg-slate-700 text-slate-300 hover:border-emerald-300'
                  }`}
                >
                  Тёмный
                </button>
              </div>
            </div>

            {/* Shuffle Transactions Option */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-emerald-300 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={addRandomOperations}
                  onChange={(e) => {
                    setAddRandomOperations(e.target.checked);
                    setGeneratedData(null);
                  }}
                  className="w-5 h-5 rounded border-slate-300 text-emerald-500 focus:ring-emerald-500"
                />
                <div>
                  <div className="font-medium text-slate-700">Рандомные операции</div>
                  <div className="text-xs text-slate-500">
                    Добавить 5-6 случайных операций + первый депозит СБП
                  </div>
                </div>
              </label>
            </div>

            {/* Profile Selection */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Профиль OZON</Label>
              <div className="relative">
                <button
                  data-testid="profile-dropdown"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full p-3 rounded-lg border-2 border-slate-200 bg-white text-left flex items-center justify-between hover:border-slate-300 transition-colors"
                >
                  {selectedProfile ? (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <div className="text-slate-800 font-medium">{selectedProfile.name}</div>
                        <div className="text-xs text-slate-500">
                          ID: {selectedProfile.cashinoutId} • Баланс: {(selectedProfile.template_balance || selectedProfile.balance || 0).toLocaleString()} ₽
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="text-slate-400">Выберите профиль...</span>
                  )}
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {dropdownOpen && (
                  <div className="absolute z-50 w-full mt-2 py-2 bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                    {profiles.length === 0 ? (
                      <div className="px-4 py-3 text-slate-400 text-center">
                        Нет профилей
                      </div>
                    ) : (
                      profiles.map((profile) => (
                        <button
                          key={profile.id}
                          onClick={() => {
                            setSelectedProfile(profile);
                            setDropdownOpen(false);
                            setGeneratedData(null);
                          }}
                          className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors ${
                            selectedProfile?.id === profile.id ? 'bg-emerald-50' : ''
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-500" />
                          </div>
                          <div className="text-left flex-1">
                            <div className="text-slate-800">{profile.name}</div>
                            <div className="text-xs text-slate-500">
                              ID: {profile.cashinoutId} • {(profile.template_balance || profile.balance || 0).toLocaleString()} ₽ • {(profile.transactions || []).length} транзакций
                            </div>
                          </div>
                          {selectedProfile?.id === profile.id && (
                            <Check className="w-4 h-4 text-emerald-500" />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <Button
              data-testid="generate-btn"
              onClick={handleGenerate}
              disabled={!selectedProfile || isGenerating}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            >
              {isGenerating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Wallet className="w-4 h-4" />
              )}
              Сформировать страницу
            </Button>
          </CardContent>
        </Card>

        {/* Right Panel - Preview Info */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Предпросмотр</h2>
            
            {generatedData ? (
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-slate-200">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-xl font-semibold text-slate-800">{generatedData.fullName}</div>
                    <div className="text-emerald-600 font-medium">
                      {generatedData.balance.toLocaleString()} ₽
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-slate-600">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{generatedData.phone}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-600">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span>{generatedData.email}</span>
                  </div>
                  {selectedTemplate === 'payments' && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <History className="w-4 h-4 text-slate-400" />
                      <span>{generatedData.transactions.length} транзакций</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    data-testid="open-preview-btn"
                    onClick={openPreview}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Открыть
                  </Button>
                  <Button
                    data-testid="copy-link-btn"
                    variant="outline"
                    onClick={copyLink}
                    className="gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Копировать
                  </Button>
                </div>
                
                {/* Download Archive Button */}
                <Button 
                  onClick={downloadArchive}
                  disabled={isDownloadingArchive}
                  variant="outline"
                  className="w-full mt-3"
                  data-testid="download-archive-btn"
                >
                  {isDownloadingArchive ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Скачать архив
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Bitcoin className="w-16 h-16 text-slate-200 mb-4" />
                <div className="text-slate-400">
                  Выберите профиль и нажмите<br />"Сформировать страницу"
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
